import { h } from 'preact'
import { useController } from 'react-hook-form'
import { useDispatch, useSelector } from 'react-redux'
import PropTypes from 'prop-types'
import { useEffect, useState } from 'preact/compat'
import { CARD_FIELDS, CARD_TYPES } from '../../../../constants/card'
import { clearBalanceData } from '../../../../redux/slice/prePaymentSlice'
import Input from '../../../Common/Input'
import { useTranslations } from '../../../../hooks/useTranslations'
import { AUTOCOMPLETE_VALUES } from '../../../../constants/autocompleteValues'
import { CardValidators } from './validators'
import VisaLogo from '../../../../assets/icons/Visa-Logo.svg'
import MastercardLogo from '../../../../assets/icons/Mastercard-Logo.svg'
import MaestroLogo from '../../../../assets/icons/Maestro-Logo.svg'
import AmericanExpressLogo from '../../../../assets/icons/American-Express-Logo.svg'
import {
  getFormattedCardNumber,
  getSelectionText,
} from '../../../../helpers/common'
import { transactionDetailsSelector } from '../../../../redux/slice/transactionDetailsSlice'
import { selectIsPciFormEnabled } from '../../../../redux/selectors/evervault'
import {
  getCleanedCardNumber,
  MAESTRO_CARD_CHECKER,
} from '../../../../helpers/cardType'

const icons = [
  {
    src: MastercardLogo,
    type: CARD_TYPES.masterCard,
  },
  {
    src: AmericanExpressLogo,
    type: CARD_TYPES.amex,
  },
  {
    src: VisaLogo,
    type: CARD_TYPES.visa,
  },
  {
    src: MaestroLogo,
    type: CARD_TYPES.maestro,
  },
]

const CardNumber = ({
  setValue,
  errors,
  control,
  trigger,
  cardBrand,
  setCardBrand,
  getValues,
  loading,
}) => {
  const l10n = useTranslations()
  const dispatch = useDispatch()
  const [brandIcon, setBrandIcon] = useState('')
  CardValidators.setTranslator(l10n)

  const { prohibitedCardBrands, tenantId } = useSelector(
    transactionDetailsSelector,
  )
  const isPciEnabled = useSelector(selectIsPciFormEnabled)

  const checkProhibitedCardBrands = CardValidators.checkProhibitedCardBrands(
    prohibitedCardBrands,
    cardBrand,
    tenantId,
  )

  const {
    field: { onChange, onBlur, value, ...props },
  } = useController({
    name: CARD_FIELDS.number,
    control,
    rules: {
      required: l10n.t('Please enter a card number'),
      validate: {
        checkProhibitedCardBrands,
        isCardValid: CardValidators.isCardValid,
        luhnAlgorithmCheck: CardValidators.luhnAlgorithmCheck,
      },
    },
  })

  const handleClipboardPaste = e => {
    e.preventDefault()
    const cardNumber = e?.clipboardData?.getData('Text')
    setValue(CARD_FIELDS.number, getFormattedCardNumber(cardNumber))
  }

  const handleBlur = async e => {
    const nextValue = e.target.value
    if (nextValue.toString().length >= 4) {
      await getBrandIcon(nextValue)
      setTimeout(() => trigger(CARD_FIELDS.number))
    } else {
      setBrandIcon('')
    }
    if (MAESTRO_CARD_CHECKER.test(getCleanedCardNumber(nextValue))) {
      trigger(CARD_FIELDS.cvv)
    }
    setValue(CARD_FIELDS.number, getFormattedCardNumber(nextValue))
    onBlur(e)
  }

  const getIconByType = (icons, type) => icons.find(el => el.type === type)

  const getBrandIcon = async value => {
    const TWO_SYMBOLS = +value.slice(0, 2)
    const FOUR_SYMBOLS = +value.slice(0, 4)
    const FIRST_SYMBOL = +value.slice(0, 1)
    if (TWO_SYMBOLS === 34 || TWO_SYMBOLS === 37) {
      setCardBrand('AmEx')
      setBrandIcon(getIconByType(icons, CARD_TYPES.amex))
    }
    if (FIRST_SYMBOL === 4) {
      setBrandIcon(getIconByType(icons, CARD_TYPES.visa))
      setCardBrand('Visa')
    }
    if (
      TWO_SYMBOLS === 50 ||
      (TWO_SYMBOLS >= 56 && TWO_SYMBOLS <= 58) ||
      FIRST_SYMBOL === 6
    ) {
      setCardBrand('Maestro')
      setBrandIcon(getIconByType(icons, CARD_TYPES.maestro))
    }
    if (
      (FOUR_SYMBOLS >= 2221 && FOUR_SYMBOLS <= 2720) ||
      (TWO_SYMBOLS >= 51 && TWO_SYMBOLS <= 55)
    ) {
      setCardBrand('MasterCard')
      setBrandIcon(getIconByType(icons, CARD_TYPES.masterCard))
    }

    if (/^(677713|533944)\d{5,}$/.test(value.replace(/\s/g, ''))) {
      setCardBrand('GiftCard')
    }

    if (/^40006506\d{8}$/.test(value.replace(/\s/g, ''))) {
      setCardBrand('Omnio')
      setBrandIcon('')
    } else {
      if (isPciEnabled) {
        window.parent?.postMessage?.(
          {
            type: 'MFEStoreDispatch',
            data: {
              actionType: 'PaymentsMFEOmnioClearBalanceData',
            },
          },
          '*',
        )
      }
      dispatch(clearBalanceData())
    }
  }

  const handleChange = e => {
    const nextValue = e.target.value
    const rule = new RegExp(/^[\d\s]*$/)
    if (nextValue.toString().length >= 4) {
      getBrandIcon(nextValue)
    } else {
      setCardBrand('')
      setBrandIcon('')
    }
    if (!rule.test(nextValue)) {
      onChange(rule.test(value) ? value : '')
      return
    }
    const nextFormattedValue = getFormattedCardNumber(nextValue)
    onChange(nextFormattedValue)
  }

  const maxLength = 19

  const handleKeyPress = e => {
    const val = e?.target?.value
    const cardNumber = val.replace(/\s/g, '')

    const lengthValidation =
      cardNumber?.length >= maxLength &&
      getSelectionText().length !== val.length

    if (lengthValidation) {
      e.preventDefault()
    }
  }

  useEffect(async () => {
    const cardNumber = getValues(CARD_FIELDS.number)
    if (cardNumber) {
      await getBrandIcon(cardNumber)
    }
  }, [])

  return (
    <Input
      label={l10n.t('Card number')}
      inputProps={{
        'data-apexx': 'card_number',
        'data-testid': 'card-number',
        name: 'card_number',
        onPaste: handleClipboardPaste,
        onChange: handleChange,
        onBlur: handleBlur,
        onKeyPress: handleKeyPress,
        id: CARD_FIELDS.number,
        value,
        inputmode: 'numeric',
        autocomplete: AUTOCOMPLETE_VALUES.CARD_NUMBER,
        tabIndex: 0,
        ...props,
      }}
      hasError={Boolean(errors[CARD_FIELDS.number]?.message)}
      errorMessage={errors[CARD_FIELDS.number]?.message}
      brandIcon={
        <img
          className="bran-logo-image"
          src={brandIcon?.src || ''}
          alt={brandIcon?.type || ''}
          key={brandIcon.type}
          data-testid={`${brandIcon.type}-icon`}
        />
      }
      loading={loading}
    />
  )
}

CardNumber.propTypes = {
  control: PropTypes.any.isRequired,
  setValue: PropTypes.func.isRequired,
  errors: PropTypes.object.isRequired,
}

export default CardNumber
